/**
 * Header — Top bar (48px) with breadcrumbs, cost badge, and user avatar dropdown.
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import { colors, topBar, creditsColor } from '../../theme';
import {
  ChevronRight,
  User,
  Settings,
  LogOut,
  ArrowUpRight,
  CheckCircle,
  Shield,
} from 'lucide-react';
import { SidebarMobileTrigger } from './sidebar';

// ─── Types ─────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export type UserTier = 'regular' | 'managed' | 'enterprise';

const TIER_CONFIG: Record<UserTier, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  regular: { label: 'Regular', color: 'text-slate-400', bgColor: 'bg-slate-500/15', icon: <User className="h-3 w-3" /> },
  managed: { label: 'Managed', color: 'text-emerald-400', bgColor: 'bg-emerald-500/15', icon: <CheckCircle className="h-3 w-3" /> },
  enterprise: { label: 'Enterprise', color: 'text-amber-400', bgColor: 'bg-amber-500/15', icon: <Shield className="h-3 w-3" /> },
};

export interface HeaderProps {
  /** Breadcrumb segments */
  breadcrumbs?: BreadcrumbItem[];
  /** Credits balance (displayed in cost badge) */
  creditsBalance?: number;
  /** Current user tier */
  userTier?: UserTier;
  /** Current user name */
  userName?: string;
  /** Current user avatar URL */
  userAvatar?: string;
  /** Navigate handler for breadcrumbs and menu items */
  onNavigate?: (href: string) => void;
  /** Logout handler */
  onLogout?: () => void;
  /** Mobile sidebar toggle */
  onMobileSidebarToggle?: () => void;
  /** Custom class */
  className?: string;
}

// ─── Component ─────────────────────────────────────────────────────

export function Header({
  breadcrumbs = [],
  creditsBalance,
  userTier = 'regular',
  userName,
  userAvatar,
  onNavigate,
  onLogout,
  onMobileSidebarToggle,
  className,
}: HeaderProps) {
  const [avatarMenuOpen, setAvatarMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu on outside click
  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setAvatarMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={cn(
        'flex items-center justify-between px-4 md:px-6',
        topBar.height,
        topBar.bg,
        topBar.border,
        className,
      )}
    >
      {/* Left: Mobile hamburger + Breadcrumbs */}
      <div className="flex items-center gap-2">
        {onMobileSidebarToggle && (
          <SidebarMobileTrigger onClick={onMobileSidebarToggle} />
        )}

        <nav className="flex items-center gap-1.5 text-sm">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <ChevronRight className="h-3.5 w-3.5 text-slate-600" />
              )}
              {crumb.href ? (
                <button
                  onClick={() => onNavigate?.(crumb.href!)}
                  className={cn(colors.text.secondary, 'hover:text-slate-200 transition-colors')}
                >
                  {crumb.label}
                </button>
              ) : (
                <span className={colors.text.body}>{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Right: Tier badge + Upgrade CTA + Cost badge + Avatar dropdown */}
      <div className="flex items-center gap-3">
        {/* Tier Badge */}
        {userTier && (
          <div className={cn(
            'hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
            TIER_CONFIG[userTier].bgColor,
            TIER_CONFIG[userTier].color,
          )}>
            {TIER_CONFIG[userTier].icon}
            {TIER_CONFIG[userTier].label}
          </div>
        )}

        {/* Upgrade CTA — shown only for regular tier */}
        {userTier === 'regular' && (
          <a
            href="https://axonbos.com/pricing"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
              'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white',
              'hover:from-emerald-500 hover:to-emerald-400 hover:shadow-lg hover:shadow-emerald-500/20',
            )}
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
            Upgrade to Managed
          </a>
        )}

        {/* Cost Badge */}
        {creditsBalance !== undefined && (
          <div className="hidden sm:flex items-center gap-1.5 text-xs">
            <span className={colors.text.secondary}>Credits:</span>
            <span className={cn('font-medium', creditsColor(creditsBalance))}>
              ₹{creditsBalance.toFixed(2)}
            </span>
          </div>
        )}

        {/* Avatar Dropdown */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {userAvatar ? (
              <img
                src={userAvatar}
                alt={userName ?? 'User'}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
                <User className="h-4 w-4" />
              </div>
            )}
          </button>

          {avatarMenuOpen && (
            <div className={cn(
              'absolute right-0 top-full z-50 mt-2 w-48 rounded-md border shadow-lg py-1',
              colors.bg.card,
              colors.border.default,
            )}>
              {userName && (
                <div className="px-3 py-2 border-b border-slate-700">
                  <p className="text-sm font-medium text-slate-200">{userName}</p>
                </div>
              )}
              <button
                onClick={() => {
                  onNavigate?.('/settings?tab=profile');
                  setAvatarMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700/50 transition-colors"
              >
                <User className="h-4 w-4" />
                Profile
              </button>
              <button
                onClick={() => {
                  onNavigate?.('/settings');
                  setAvatarMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700/50 transition-colors"
              >
                <Settings className="h-4 w-4" />
                Settings
              </button>
              {onLogout && (
                <>
                  <div className="border-t border-slate-700 my-1" />
                  <button
                    onClick={() => {
                      onLogout();
                      setAvatarMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-400 hover:bg-slate-700/50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
